import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import {
    Alert,
    Box,
    Button,
    Card,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    Paper,
    Snackbar,
    TextField,
    Typography,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import Page from "@/components/Page";
import type { PermissionGroup } from "@/types/permissions.types";

import PermissionGroupDialog from "./PermissionGroupDialog";
import { usePermissions } from "./permissions.hooks";

const PermissionsPage = () => {
    const { t } = useTranslation();
    const {
        selectedGroup,
        workingGroup,
        setSelectedGroup,
        searchTerm,
        setSearchTerm,
        filteredGroups,
        handleLocalPermissionChange,
        handleDeleteGroup,
        handleAddGroup,
        handleUpdateGroup,
        permissionCodes,
        isLoading,
        error,
        hasLocalChanges,
        saveChanges,
        cancelChanges,
    } = usePermissions();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingGroup, setEditingGroup] = useState<PermissionGroup | undefined>(undefined);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState<PermissionGroup | null>(null);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);

    const handleOpenDialog = (group?: PermissionGroup) => {
        setEditingGroup(group);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setEditingGroup(undefined);
        setDialogOpen(false);
    };

    const handleSaveGroup = (group: Omit<PermissionGroup, "id">) => {
        if (editingGroup) {
            handleUpdateGroup({ ...group, id: editingGroup.id });
            setSuccessMessage("Permission group updated successfully");
        } else {
            handleAddGroup(group);
            setSuccessMessage("Permission group added successfully");
        }
        setShowSuccessMessage(true);
        handleCloseDialog();
    };

    const handleDeleteClick = (group: PermissionGroup) => {
        setGroupToDelete(group);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = () => {
        if (groupToDelete) {
            handleDeleteGroup(groupToDelete.id);
            setSuccessMessage("Permission group deleted successfully");
            setShowSuccessMessage(true);
        }
        setDeleteDialogOpen(false);
        setGroupToDelete(null);
    };

    const handleCancelDelete = () => {
        setDeleteDialogOpen(false);
        setGroupToDelete(null);
    };

    const handleSaveChanges = () => {
        saveChanges();
        setSuccessMessage("Permission group updated successfully");
        setShowSuccessMessage(true);
    };

    const handleCancelChanges = () => {
        setShowUnsavedChangesDialog(true);
    };

    const confirmCancelChanges = () => {
        cancelChanges();
        setShowUnsavedChangesDialog(false);
        setSuccessMessage("Changes discarded");
        setShowSuccessMessage(true);
    };

    // Loading display
    if (isLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", p: 5 }}>
                <CircularProgress />
            </Box>
        );
    }

    // Error display
    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">Error loading permission data. Please try again later.</Alert>
            </Box>
        );
    }

    // Displaying permissions in a service card with compact styling
    const renderServicePermissions = (serviceName: string) => {
        if (!workingGroup || !permissionCodes) {
            return null;
        }

        const serviceData = permissionCodes[serviceName];
        const hasMessage = serviceData && "message" in serviceData;

        return (
            <Grid item xs={12} md={6} key={serviceName}>
                <Card sx={{ p: 1.5, height: "100%", display: "flex", flexDirection: "column" }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontSize: "0.875rem" }}>
                        {serviceName}
                    </Typography>
                    {hasMessage ? (
                        <Alert severity="info" sx={{ width: "100%", py: 0.5, fontSize: "0.75rem" }}>
                            {t("no_available_permission_codes_for_this_service")}
                        </Alert>
                    ) : (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.25,
                                maxHeight: "180px",
                                overflowY: "auto",
                                flex: 1,
                            }}
                        >
                            {serviceData &&
                                Object.keys(serviceData)
                                    .sort((a, b) => a.localeCompare(b))
                                    .map((permission) => (
                                        <FormControlLabel
                                            key={permission}
                                            control={
                                                <Checkbox
                                                    checked={workingGroup.permissions.includes(serviceData[permission])}
                                                    onChange={(e) =>
                                                        handleLocalPermissionChange(
                                                            serviceData[permission],
                                                            e.target.checked
                                                        )
                                                    }
                                                    size="small"
                                                    sx={{ p: 0.25 }}
                                                />
                                            }
                                            label={
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontSize: "0.7rem",
                                                        wordBreak: "break-all",
                                                        lineHeight: 1.1,
                                                        maxWidth: "100%",
                                                        display: "block",
                                                    }}
                                                >
                                                    {permission}
                                                </Typography>
                                            }
                                            sx={{ margin: 0, ml: 0.5 }}
                                        />
                                    ))}
                        </Box>
                    )}
                </Card>
            </Grid>
        );
    };

    return (
        <Page title={t("permissions")}>
            <Grid container spacing={3}>
                {/* Left Panel */}
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, height: "100%" }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            {t("permission_groups")}
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="Search..."
                            variant="outlined"
                            size="small"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            sx={{ mb: 1 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <List sx={{ overflowY: "auto", py: 1 }}>
                            {filteredGroups
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((group) => (
                                    <ListItem
                                        key={group.id}
                                        button
                                        selected={selectedGroup?.id === group.id}
                                        onClick={() => setSelectedGroup(group)}
                                        sx={{
                                            borderRadius: 1,
                                            mb: 1.5,
                                            py: 1,
                                            backgroundColor: group.color,
                                            color: "#fff",
                                            "&.Mui-selected": {
                                                backgroundColor: group.color,
                                                opacity: 0.8,
                                                "&:hover": {
                                                    backgroundColor: group.color,
                                                    opacity: 0.7,
                                                },
                                            },
                                            "&:hover": {
                                                backgroundColor: group.color,
                                                opacity: 0.9,
                                            },
                                        }}
                                    >
                                        <ListItemText
                                            primary={group.name}
                                            primaryTypographyProps={{
                                                fontSize: "1rem",
                                                fontWeight: 500,
                                                style: {
                                                    textOverflow: "ellipsis",
                                                    overflow: "hidden",
                                                    whiteSpace: "nowrap",
                                                },
                                            }}
                                        />
                                    </ListItem>
                                ))}
                        </List>
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<AddIcon />}
                            sx={{ mt: 1 }}
                            size="small"
                            onClick={() => handleOpenDialog()}
                        >
                            {t("add_permission_group")}
                        </Button>
                    </Paper>
                </Grid>

                {/* Right Panel */}
                <Grid item xs={12} md={8}>
                    {workingGroup ? (
                        <Paper sx={{ p: 2 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mb: 2,
                                }}
                            >
                                <Typography variant="h6">
                                    {t("permission_group")}: {workingGroup.name}
                                    {hasLocalChanges && (
                                        <Typography
                                            component="span"
                                            sx={{ ml: 1, color: "warning.main", fontSize: "0.7rem" }}
                                        >
                                            ({t("unsaved_changes")})
                                        </Typography>
                                    )}
                                </Typography>
                                <Box>
                                    <IconButton
                                        color="primary"
                                        onClick={() => handleOpenDialog(workingGroup)}
                                        size="small"
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    {hasLocalChanges ? (
                                        <>
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                size="small"
                                                onClick={handleCancelChanges}
                                                sx={{ mr: 1, fontSize: "0.75rem", py: 0.5 }}
                                            >
                                                {t("cancel")}
                                            </Button>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                size="small"
                                                onClick={handleSaveChanges}
                                                sx={{ fontSize: "0.75rem", py: 0.5 }}
                                            >
                                                {t("save")}
                                            </Button>
                                        </>
                                    ) : (
                                        <IconButton
                                            color="error"
                                            onClick={() => handleDeleteClick(workingGroup)}
                                            size="small"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                            </Box>

                            {/* Permissions */}
                            <Grid container spacing={2} alignItems="stretch">
                                {permissionCodes &&
                                    Object.keys(permissionCodes)
                                        .sort((a, b) => a.localeCompare(b))
                                        .map((serviceName) => renderServicePermissions(serviceName))}
                            </Grid>
                        </Paper>
                    ) : (
                        <Paper
                            sx={{
                                p: 2,
                                height: "100%",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                flexDirection: "column",
                                gap: 2,
                            }}
                        >
                            <Typography variant="h6">{t("no_permission_group_selected")}</Typography>
                            <Button variant="contained" color="primary" onClick={() => handleOpenDialog()}>
                                {t("add_permission_group")}
                            </Button>
                        </Paper>
                    )}
                </Grid>
            </Grid>

            <PermissionGroupDialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                onSave={handleSaveGroup}
                editGroup={editingGroup}
                permissionCodes={permissionCodes}
                isLoading={isLoading}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
                <DialogTitle>{t("delete_permission_group")}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {t("are_you_sure_you_want_to_delete_the_permission_group")} "{groupToDelete?.name}"?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelDelete}>{t("cancel")}</Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained">
                        {t("delete")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Discard Changes Confirmation Dialog */}
            <Dialog open={showUnsavedChangesDialog} onClose={() => setShowUnsavedChangesDialog(false)}>
                <DialogTitle>{t("discard_changes")}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {t("you_have_unsaved_changes")}. {t("are_you_sure_you_want_to_discard_them")}?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowUnsavedChangesDialog(false)}>{t("back")}</Button>
                    <Button onClick={confirmCancelChanges} color="error" variant="contained">
                        {t("discard_changes")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success Message */}
            <Snackbar
                open={showSuccessMessage}
                autoHideDuration={3000}
                onClose={() => setShowSuccessMessage(false)}
                message={successMessage}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            />
        </Page>
    );
};

export default PermissionsPage;
